import { gql } from '@apollo/client';

export const PUBLICATIONS_QUERY = gql`
  query Publications {
    publications {
      id
      title
      author
      year
      public
      figuresCount
      createdAt
      user {
        name
      }
      pages {
        id
        number
      }
    }
  }
`;

export const PAGE_QUERY = gql`
  query Page($id: ID!) {
    page(id: $id) {
      id
      number
      publicationId
      imageUrl
      figures {
        id
        pageId
        x1
        y1
        x2
        y2
        type
        probability
        verified
        disturbed
        depositionType
        contour
        manualBoundingBox
        anchorPoint1X
        anchorPoint1Y
        anchorPoint2X
        anchorPoint2Y
        anchorPoint3X
        anchorPoint3Y
        anchorPoint4X
        anchorPoint4Y
        controlPoint1X
        controlPoint1Y
        controlPoint2X
        controlPoint2Y
        controlPoint3X
        controlPoint3Y
        controlPoint4X
        controlPoint4Y
      }
    }
  }
`;

export const FIGURES_QUERY = gql`
  query Figures($pageId: ID, $type: String) {
    figures(pageId: $pageId, type: $type) {
      id
      pageId
      x1
      y1
      x2
      y2
      type
      probability
      verified
      disturbed
      depositionType
    }
  }
`;

export const GRAVES_QUERY = gql`
  query Graves($publicationId: Int, $siteId: Int, $sort: String, $offset: Int, $first: Int) {
    graves(
      publicationId: $publicationId
      siteId: $siteId
      sort: $sort
      offset: $offset
      first: $first
    ) {
      id
      identifier
      areaWithUnit
      perimeter
      perimeterWithUnit
      width
      widthWithUnit
      height
      heightWithUnit
      boundingBoxWidth
      boundingBoxWidthWithUnit
      boundingBoxHeight
      boundingBoxHeightWithUnit
      angle
      verified
      disturbed
      publicationId
      siteId
      tags {
        id
        name
      }
      arrow {
        angle
      }
      publication {
        id
        author
        year
      }
      site {
        id
        name
      }
      createdAt
    }
  }
`;

export const GRAVES_COUNT_QUERY = gql`
  query GravesCount($publicationId: Int, $siteId: Int) {
    gravesCount(publicationId: $publicationId, siteId: $siteId)
  }
`;

export const CERAMICS_QUERY = gql`
  query Ceramics(
    $publicationId: Int
    $siteId: Int
    $tagIds: [Int!]
    $verified: Boolean
    $sort: String
    $offset: Int
    $first: Int
  ) {
    ceramics(
      publicationId: $publicationId
      siteId: $siteId
      tagIds: $tagIds
      verified: $verified
      sort: $sort
      offset: $offset
      first: $first
    ) {
      id
      identifier
      area
      perimeter
      probability
      scale
      width
      height
      verified
      page {
        number
      }
      publication {
        id
        title
      }
      site {
        id
        name
      }
      tags {
        name
      }
      createdAt
    }
  }
`;

export const CERAMICS_COUNT_QUERY = gql`
  query CeramicsCount($publicationId: Int, $siteId: Int, $tagIds: [Int!], $verified: Boolean) {
    ceramicsCount(
      publicationId: $publicationId
      siteId: $siteId
      tagIds: $tagIds
      verified: $verified
    )
  }
`;

export const LITHICS_QUERY = gql`
  query Lithics(
    $publicationId: Int
    $siteId: Int
    $tagIds: [Int!]
    $verified: Boolean
    $sort: String
    $offset: Int
    $first: Int
  ) {
    lithics(
      publicationId: $publicationId
      siteId: $siteId
      tagIds: $tagIds
      verified: $verified
      sort: $sort
      offset: $offset
      first: $first
    ) {
      id
      identifier
      area
      perimeter
      probability
      x1
      y1
      x2
      y2
      verified
      page {
        number
      }
      publication {
        id
        title
      }
      site {
        id
        name
      }
      tags {
        name
      }
      createdAt
    }
  }
`;

export const LITHICS_COUNT_QUERY = gql`
  query LithicsCount($publicationId: Int, $siteId: Int, $tagIds: [Int!], $verified: Boolean) {
    lithicsCount(
      publicationId: $publicationId
      siteId: $siteId
      tagIds: $tagIds
      verified: $verified
    )
  }
`;

export const SITES_QUERY = gql`
  query Sites {
    sites {
      id
      name
      gravesCount
      figuresCount
    }
  }
`;

// Sites with coordinates, for the region picker in the analytics query builder.
export const POSITIONED_SITES_QUERY = gql`
  query PositionedSites {
    sites {
      id
      name
      lat
      lon
    }
  }
`;

export const TAGS_QUERY = gql`
  query Tags {
    tags {
      id
      name
      figuresCount
    }
  }
`;

export const USERS_QUERY = gql`
  query Users {
    users {
      id
      name
      email
      role
      active
      createdAt
    }
  }
`;

export const TEAMS_QUERY = gql`
  query Teams {
    teams {
      id
      name
      membersCount
      publicationsCount
      createdAt
    }
  }
`;

export const AVAILABLE_TEAM_MEMBERS_QUERY = gql`
  query AvailableTeamMembers($teamId: ID!) {
    availableTeamMembers(teamId: $teamId) {
      id
      name
      email
    }
  }
`;

export const AVAILABLE_TEAM_PUBLICATIONS_QUERY = gql`
  query AvailableTeamPublications($teamId: ID!) {
    availableTeamPublications(teamId: $teamId) {
      id
      title
      author
      year
    }
  }
`;

export const ARROW_HEADS_QUERY = gql`
  query ArrowHeads {
    arrow_heads {
      id
      identifier
      angle
      area
      perimeter
      probability
      page {
        number
      }
      publication {
        id
        title
      }
      tags {
        name
      }
      createdAt
    }
  }
`;

export const SKELETONS_QUERY = gql`
  query Skeletons {
    skeletons {
      id
      identifier
      deposition_type
      area
      perimeter
      probability
      page {
        number
      }
      publication {
        id
        title
      }
      tags {
        name
      }
      createdAt
    }
  }
`;

export const API_TOKENS_QUERY = gql`
  query ApiTokens {
    apiTokens {
      id
      name
      lastUsedAt
      revoked
      createdAt
    }
  }
`;

// ===== Single Item Queries =====
export const PUBLICATION_QUERY = gql`
  query Publication($id: ID!) {
    publication(id: $id) {
      id
      title
      author
      year
      public
      createdAt
      user {
        name
      }
      pages {
        id
        number
      }
      figuresCount
    }
  }
`;

export const GRAVE_QUERY = gql`
  query Grave($id: ID!) {
    grave(id: $id) {
      id
      identifier
      x1
      y1
      x2
      y2
      area
      perimeter
      angle
      verified
      disturbed
      depositionType
      siteId
      publicationId
      publication {
        id
        title
        author
        year
      }
      site {
        id
        name
      }
      tags {
        id
        name
      }
      goods {
        id
        type
      }
      createdAt
    }
  }
`;

export const GRAVE_FOR_EDIT_QUERY = gql`
  query GraveForEdit($id: ID!) {
    grave(id: $id) {
      id
      x1
      x2
      y1
      y2
      identifier
      siteId
      depositionType
      verified
      disturbed
      internmentType
      percentageScale
      pageSize
      contourInfo
      tags {
        id
        name
      }
      publication {
        id
        title
        author
        year
      }
      site {
        id
        name
      }
      arrow {
        id
        angle
        x1
        y1
        x2
        y2
        page {
          id
          image {
            id
            url
            width
            height
          }
        }
      }
      scale {
        id
        text
        x1
        y1
        x2
        y2
        contour
        type
        manualBoundingBox
        boundingBoxAngle
        boundingBoxWidth
        boundingBoxHeight
        controlPoint1X
        controlPoint1Y
        controlPoint2X
        controlPoint2Y
        controlPoint3X
        controlPoint3Y
        controlPoint4X
        controlPoint4Y
        anchorPoint1X
        anchorPoint1Y
        anchorPoint2X
        anchorPoint2Y
        anchorPoint3X
        anchorPoint3Y
        anchorPoint4X
        anchorPoint4Y
      }
      skeletonFigures {
        id
        depositionType
      }
      figures {
        id
        type
        x1
        y1
        x2
        y2
        angle
        identifier
      }
      page {
        id
        number
        image {
          id
          url
          width
          height
        }
      }
    }
  }
`;

export const FIGURE_QUERY = gql`
  query Figure($id: ID!) {
    figure(id: $id) {
      id
      identifier
      type
      x1
      y1
      x2
      y2
      area
      perimeter
      angle
      probability
      verified
      disturbed
      siteId
      publicationId
      pageId
      publication {
        id
        title
        author
      }
      page {
        number
      }
      site {
        id
        name
      }
      tags {
        id
        name
      }
      percentageScale
      pageSize
      createdAt
    }
  }
`;

export const FIGURE_FOR_EDIT_QUERY = gql`
  query FigureForEdit($id: ID!) {
    figure(id: $id) {
      id
      identifier
      type
      x1
      y1
      x2
      y2
      area
      perimeter
      contour
      angle
      probability
      verified
      disturbed
      siteId
      publicationId
      pageId
      publication {
        id
        title
        author
        year
      }
      page {
        number
        image {
          id
          url
          width
          height
        }
      }
      site {
        id
        name
      }
      tags {
        id
        name
      }
      percentageScale
      pageSize
      createdAt
      scale {
        id
        type
        x1
        y1
        x2
        y2
        identifier
        text
        contour
        percentageScale
        pageSize
      }
      figures {
        id
        type
        x1
        y1
        x2
        y2
        identifier
        manualBoundingBox
        boundingBoxAngle
        boundingBoxWidth
        boundingBoxHeight
        controlPoint1X
        controlPoint1Y
        controlPoint2X
        controlPoint2Y
        controlPoint3X
        controlPoint3Y
        controlPoint4X
        controlPoint4Y
        anchorPoint1X
        anchorPoint1Y
        anchorPoint2X
        anchorPoint2Y
        anchorPoint3X
        anchorPoint3Y
        anchorPoint4X
        anchorPoint4Y
      }
    }
  }
`;

export const NEXT_FIGURE_QUERY = gql`
  query NextFigure($id: ID!, $publicationId: Int, $type: String!) {
    nextFigure(id: $id, publicationId: $publicationId, type: $type) {
      id
    }
  }
`;

export const NEXT_GRAVE_QUERY = gql`
  query NextGrave($id: ID!, $publicationId: Int) {
    nextGrave(id: $id, publicationId: $publicationId) {
      id
    }
  }
`;

export const SITE_QUERY = gql`
  query Site($id: ID!) {
    site(id: $id) {
      id
      name
      lat
      lon
      locality
      countryCode
      siteCode
      gravesCount
      figuresCount
      createdAt
    }
  }
`;

export const USER_QUERY = gql`
  query User($id: ID!) {
    user(id: $id) {
      id
      name
      email
      role
      role
      disabled
      createdAt
    }
  }
`;

export const TEAM_QUERY = gql`
  query Team($id: ID!) {
    team(id: $id) {
      id
      name
      description
      users {
        id
        name
        email
      }
      publications {
        id
        title
        author
      }
      createdAt
    }
  }
`;

export const TAG_QUERY = gql`
  query Tag($id: ID!) {
    tag(id: $id) {
      id
      name
    }
  }
`;

// Point-prompt SAM contour of a figure (legacy /size_figures/:id/sam_contour
// folded into GraphQL).
export const SAM_CONTOUR_QUERY = gql`
  query SamContour($id: ID!, $points: JSON!) {
    samContour(id: $id, points: $points) {
      contour
    }
  }
`;

// ===== Kiosk queries (legacy /kiosk_configs/* routes folded into GraphQL) =====

export const KIOSK_CONFIG_QUERY = gql`
  query KioskConfig {
    kioskConfig {
      id
      pageId
      figureId
      publicationId
    }
  }
`;

export const KIOSK_PUBLICATIONS_QUERY = gql`
  query KioskPublications {
    kioskPublications {
      id
      title
      author
    }
  }
`;

export const KIOSK_PAGES_QUERY = gql`
  query KioskPages($publicationId: ID!) {
    kioskPages(publicationId: $publicationId) {
      id
      pageNumber
      imageUrl
    }
  }
`;

export const KIOSK_PAGE_FIGURES_QUERY = gql`
  query KioskPageFigures($pageId: ID!) {
    kioskPageFigures(pageId: $pageId) {
      id
      type
      x1
      y1
      x2
      y2
      probability
      identifier
      contour
      imageUrl
    }
  }
`;

export const GRAVE_RELATIONS_QUERY = gql`
  query GraveRelations($id: ID!) {
    grave(id: $id) {
      id
      identifier
      page {
        id
        number
        image {
          id
          url
          width
          height
        }
        figures {
          id
          type
          identifier
          x1
          y1
          x2
          y2
        }
      }
      goods {
        id
        identifier
        type
        x1
        y1
        x2
        y2
        artefacts {
          id
          identifier
          type
        }
        ceramics {
          id
          identifier
          type
        }
        lithics {
          id
          identifier
          type
        }
      }
    }
  }
`;
